/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {isPoliticalPreviewCreative} from './preview-language';

const CONTEXTUAL_TARGET_PATHS = [
  'keywordTargets',
  'similarKeywordTargets',
  'categoryTargets',
  'semanticTargets',
  'keyValueTargets',
  'articleTarget.urls'
];

const LOCATION_TARGET_PATHS = [
  'namedLocationTarget.locations',
  'geospatialTargets'
];

const USER_TARGET_PATHS = [
  'userSegmentTargets',
  'retargetingTargets',
  'ipAddressTarget.addresses',
  'firstPartyAudienceTarget.audienceIds',
  'firstPartyAudienceTarget.firstPartyAudiences'
];

function hasConfiguredTarget(targeting: any, path: string): boolean {
  const value = _.get(targeting, path);

  if (_.isArray(value)) {
    return _.some(value, (entry: any) => {
      if (_.isArray(entry)) {
        return entry.length > 0;
      }
      if (_.isObject(entry)) {
        return !_.isEmpty(entry);
      }
      return !_.isNil(entry) && entry !== '';
    });
  }

  if (_.isObject(value)) {
    return _.some(value, (entry: any) => {
      if (_.isArray(entry)) {
        return entry.length > 0;
      }
      return !_.isNil(entry) && entry !== '';
    });
  }

  return !_.isNil(value) && value !== '';
}

function hasTargetingPaths(targeting: any, paths: string[]): boolean {
  return _.some(paths, (path: string) => hasConfiguredTarget(targeting, path));
}

function getPreviewTargetingSources(creative: any, lineItem: any): any[] {
  const sources: any[] = [];
  const seen = new Set();

  _.forEach([
    _.get(creative, 'targeting'),
    _.get(lineItem, 'targeting'),
    _.get(creative, ['lineItem', 'targeting'])
  ], (targeting: any) => {
    if (targeting && !seen.has(targeting)) {
      seen.add(targeting);
      sources.push(targeting);
    }
  });

  return sources;
}

function hasTargetingPathsFromSources(targetingSources: any[], paths: string[]): boolean {
  return _.some(targetingSources, (targeting: any) => hasTargetingPaths(targeting, paths));
}

function getElection(creative: any, lineItem: any, election: any): any {
  election = election || _.get(lineItem, 'election') || _.get(creative, ['lineItem', 'election']) || _.get(creative, 'election') || null;
  if (!election) {
    return null;
  }

  const networkElection = _.find(LocalNetworkInfo.obtain().getSuppInfo('elections') || [], (candidate: any) => {
    return (_.get(election, 'id') && _.get(candidate, 'id') === _.get(election, 'id'))
      || (_.get(election, 'name') && _.get(candidate, 'name') === _.get(election, 'name'));
  });

  return _.assign({}, networkElection || {}, election);
}

function getAdvertiserName(creative: any, lineItem: any, advertiser: any): string | null {
  advertiser = advertiser
    || _.get(lineItem, 'advertiser')
    || _.get(creative, ['lineItem', 'advertiser'])
    || _.get(creative, 'advertiser')
    || null;

  return _.get(advertiser, 'legalName') || _.get(advertiser, 'name') || null;
}

export function resolveCreativePreviewAdvertiser(
  creative: any,
  lineItem: any,
  advertiser: any,
  loadAdvertiser: (id: string) => PromiseLike<any>
): any {
  advertiser = advertiser
    || _.get(lineItem, 'advertiser')
    || _.get(creative, ['lineItem', 'advertiser'])
    || _.get(creative, 'advertiser')
    || null;

  const politicalPreview = isPoliticalPreviewCreative(creative) || isPoliticalPreviewCreative({lineItem: lineItem});
  if (!politicalPreview || _.get(advertiser, 'legalName') || !_.get(advertiser, 'id') || !_.isFunction(loadAdvertiser)) {
    return advertiser;
  }

  return loadAdvertiser(advertiser.id).then((loadedAdvertiser: any) => {
    return loadedAdvertiser || advertiser;
  }, () => {
    return advertiser;
  });
}

export function collateCreativePreviewRequestData(creative: any, lineItem: any, advertiser: any, election: any): any {
  const targetingSources = getPreviewTargetingSources(creative, lineItem);
  election = getElection(creative, lineItem, election);
  const advertiserName = getAdvertiserName(creative, lineItem, advertiser);

  const previewRequestData: any = {
    hasContextualTargeting: hasTargetingPathsFromSources(targetingSources, CONTEXTUAL_TARGET_PATHS),
    hasLocationTargeting: hasTargetingPathsFromSources(targetingSources, LOCATION_TARGET_PATHS),
    hasUserTargeting: hasTargetingPathsFromSources(targetingSources, USER_TARGET_PATHS)
  };

  if (_.get(election, 'name')) {
    previewRequestData.electionName = election.name;
  }
  if (_.get(election, 'electionDate')) {
    previewRequestData.electionDate = election.electionDate;
  }
  if (advertiserName) {
    previewRequestData.advertiserName = advertiserName;
  }

  return previewRequestData;
}

export function collateCreativePreviewLineItemData(creative: any, lineItem?: any): any {
  return _.pickBy({
    lineItemId: _.get(creative, 'lineItemId') || _.get(lineItem, 'id') || _.get(creative, ['lineItem', 'id']),
    lineItemCode: _.get(creative, 'lineItemCode') || _.get(lineItem, 'lineItemCode') || _.get(creative, ['lineItem', 'lineItemCode'])
  }, (value: any) => {
    return value !== undefined && value !== null && value !== '';
  });
}
